import { useState, useRef } from "react";
import { userPool, CognitoUser, AuthenticationDetails } from "../../config/auth";
import { getInitials } from "../../utils/formatters";
import styles from "./LoginPage.module.css";

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("login");
  const cognitoUserRef = useRef(null);

  const handleLogin = () => {
    if (!email || !password) { setError("Email and password are required"); return; }
    setLoading(true);
    setError("");
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUserRef.current = cognitoUser;
    cognitoUser.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => {
          const payload = session.getIdToken().decodePayload();
          onLogin({
            name:     payload["name"] || email,
            email:    payload["email"],
            role:     payload["custom:role"] || "readonly",
            initials: getInitials(payload["name"] || email),
            token:    session.getIdToken().getJwtToken(),
          });
          setLoading(false);
        },
        onFailure: (err) => {
          setError(err.message || "Login failed");
          setLoading(false);
        },
        newPasswordRequired: () => {
          setStage("new_password_required");
          setLoading(false);
        },
      }
    );
  };

  const handleNewPassword = () => {
    if (!newPassword || newPassword !== confirmPassword) {
      setError("Passwords do not match"); return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters"); return;
    }
    setLoading(true);
    setError("");
    cognitoUserRef.current.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => {
        const payload = session.getIdToken().decodePayload();
        onLogin({
          name:     payload["name"] || email,
          email:    payload["email"],
          role:     payload["custom:role"] || "readonly",
          initials: getInitials(payload["name"] || email),
          token:    session.getIdToken().getJwtToken(),
        });
        setLoading(false);
      },
      onFailure: (err) => {
        setError(err.message || "Password change failed");
        setLoading(false);
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.bgContainer}>
        {[...Array(20)].map((_, i) => (
          <div key={i} className={styles.bgLine} style={{
            height: `${60 + Math.random() * 200}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDuration: `${2 + Math.random() * 3}s`,
            animationDelay: `${Math.random() * 3}s`,
          }} />
        ))}
      </div>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLabel}>Vital Care of Tacoma</div>
          <div className={styles.headerTitle}>Inventory System</div>
          <div className={styles.headerSubtitle}>SECURE ACCESS PORTAL</div>
        </div>
        <div className={styles.card}>
          {stage === "login" ? (
            <>
              <div className={styles.fieldGroup}>
                <div className={styles.label}>Email</div>
                <input className={styles.input} type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@vitalcare.com" onKeyDown={e => e.key === "Enter" && handleLogin()} />
              </div>
              <div className={styles.fieldGroupLast}>
                <div className={styles.label}>Password</div>
                <input className={styles.input} type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••" onKeyDown={e => e.key === "Enter" && handleLogin()} />
              </div>
              {error && <div className={styles.error}>{error}</div>}
              <button className={styles.submitBtn} onClick={handleLogin} disabled={loading}>
                {loading ? "AUTHENTICATING..." : "ACCESS SYSTEM"}
              </button>
            </>
          ) : (
            <>
              <div className={styles.newPasswordMessage}>
                First login detected.<br />Please set a new password.
              </div>
              <div className={styles.fieldGroup}>
                <div className={styles.label}>New Password</div>
                <input className={styles.input} type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 characters" />
              </div>
              <div className={styles.fieldGroupLast}>
                <div className={styles.label}>Confirm Password</div>
                <input className={styles.input} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password" onKeyDown={e => e.key === "Enter" && handleNewPassword()} />
              </div>
              {error && <div className={styles.error}>{error}</div>}
              <button className={styles.submitBtn} onClick={handleNewPassword} disabled={loading}>
                {loading ? "SAVING..." : "SET PASSWORD & LOGIN"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
